import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress, DialogContent,
    Pagination,
    Stack, Table, TableBody, TableCell, TableRow,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {DEFAULT_EMPATHY} from "../../../config";
import React from "react";
import {useTranslation} from "react-i18next";
import {useKontaktaufnahme} from "../../../Contexts/KontaktaufnahmeContext";
import {
    AuswertungAufgabe,
    AuswertungKontaktaufnahme,
    AuswertungUnteraufgabe,
    AuswertungUnteraufgabenListe, Unteraufgabe,
    ViewKontakt,
} from "../../../api";
import StopTwilioCallButton from "./StopTwilioCallButton";
import {useQuery} from "react-query";
import {
    useGetAufgabe,
    useGetAuswertungAufgabe,
    useGetAuswertungUnteraufgabe, useGetKontaktaufnahmeTranskript,
    useGetUnteraufgabe, useGetUnteraufgabePromptWithInserts
} from "../../../client/hooks";
import {DEFAULT_LANGUAGE} from "../../../config/language";
import UnteraufgabeDetailCard from "./UnteraufgabeDetailCard";
import TabTranscriptCard from "./TabTranscriptCard";
import KontaktActionButtons from "../KontaktActionButtons";
import {ModalDialog} from "../../dialog/ModalDialog";
type UnteraufgabenAuswertungWithUnteraufgabe = AuswertungUnteraufgabe & Unteraufgabe

interface KontaktAufnahmeCardProps{
    kontaktaufnahmen?: AuswertungKontaktaufnahme[],
    kontakt: ViewKontakt,
    aufgabeId: string,
    open: boolean,
    onClose:()=> void
}

export default function KontaktCard({kontaktaufnahmen, kontakt, aufgabeId, open, onClose} : KontaktAufnahmeCardProps) {
    const {t:translation} = useTranslation("kontakte");
    const searchAuswertungAufgabe = useGetAuswertungAufgabe();
    const searchAuswertungUnteraufgaben = useGetAuswertungUnteraufgabe();
    const searchUnteraufgabe = useGetUnteraufgabe();
    const searchAufgabe = useGetAufgabe();
    const getUnteraufgabePromptWithInserts = useGetUnteraufgabePromptWithInserts();
    const { selectedIndex, setSelectedIndex, selectedKontaktaufnahme } = useKontaktaufnahme();
    const getKontaktaufnahmeTranskript = useGetKontaktaufnahmeTranskript();

    const {
        data: aufgabeData,
    } = useQuery(
        ['aufgabe', aufgabeId],
        async () => {
            if (!aufgabeId) return null;
            return await searchAufgabe(aufgabeId);
        },
        {
            enabled: open && !!aufgabeId,
            staleTime: 5 * 60 * 1000,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        }
    );

    const sprache = aufgabeData?.sprache || DEFAULT_LANGUAGE;
    
    const getStatusColor = (status: string | undefined) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'abgeschlossen':
                return 'success';
            case 'in_progress':
            case 'in_bearbeitung':
                return 'warning';
            case 'failed':
            case 'fehlgeschlagen':
                return 'error';
            default:
                return 'default';
        }
    };
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('de-DE');
    };

    const unteraufgabenQueryEnabled = open && !!aufgabeId && !!selectedKontaktaufnahme?.id && !!kontakt?.id;
    const {
        data,
        isLoading,
        isError,
        error,
    } = useQuery(
        ['unteraufgaben', aufgabeId, selectedKontaktaufnahme?.id, kontakt?.id],
        async (): Promise<{ auswertungAufgabe: AuswertungAufgabe | null; unteraufgaben: UnteraufgabenAuswertungWithUnteraufgabe[] }> => {
            if (!selectedKontaktaufnahme?.id || !kontakt?.id) return { auswertungAufgabe: null, unteraufgaben: [] };

            try {
                const auswertungAufgabe: AuswertungAufgabe = await searchAuswertungAufgabe(
                    aufgabeId,
                    selectedKontaktaufnahme?.id
                );

                const resultAuswertungUnteraufgaben: AuswertungUnteraufgabenListe =
                    await searchAuswertungUnteraufgaben(auswertungAufgabe.id);

                const result: UnteraufgabenAuswertungWithUnteraufgabe[] = [];

                for (const auswertung of resultAuswertungUnteraufgaben.items) {
                    try {
                        const unteraufgabe: Unteraufgabe = await searchUnteraufgabe(
                            aufgabeId,
                            auswertung.unteraufgabenId
                        );

                        let replacedPrompt = unteraufgabe.prompt;
                        let replacedForeignPrompt = unteraufgabe.foreignPrompt;

                        if (kontakt.kontaktId) {
                            try {
                                const promptWithInserts = await getUnteraufgabePromptWithInserts(
                                    auswertung.unteraufgabenId,
                                    kontakt.kontaktId,
                                    aufgabeId
                                );
                                replacedPrompt = promptWithInserts.prompt;
                                replacedForeignPrompt = promptWithInserts.foreignPrompt || unteraufgabe.foreignPrompt;
                            } catch (err) {
                                console.warn('Fehler beim Laden des ersetzten Prompts', err);
                            }
                        }

                        result.push({
                            ...auswertung,
                            ...unteraufgabe,
                            prompt: replacedPrompt,
                            foreignPrompt: replacedForeignPrompt,
                        });
                    } catch (err) {
                        result.push(auswertung as UnteraufgabenAuswertungWithUnteraufgabe);
                    }
                }

                result.sort((a, b) => (a.reihenfolge || 0) - (b.reihenfolge || 0));

                return { auswertungAufgabe, unteraufgaben: result };
            } catch (err: any) {
                if (err?.status === 404) {
                    console.warn('Auswertung nicht gefunden (404)', err);
                    return { auswertungAufgabe: null, unteraufgaben: [] };
                }

                throw err;
            }
        },
        {
            enabled: unteraufgabenQueryEnabled,
            staleTime: 0,
            keepPreviousData: false,
        }
    );

    const {
        data: transkriptData,
        isLoading: transkriptIsLoading,
    } = useQuery(
        ['kontaktaufnahme-transkript', selectedKontaktaufnahme?.id],
        async () => {
            if (!selectedKontaktaufnahme?.id) return null;
            return await getKontaktaufnahmeTranskript(selectedKontaktaufnahme.id);
        },
        {
            enabled: open && !!selectedKontaktaufnahme?.id,
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    );

    return (
            <ModalDialog
                open={open}
                onClose={onClose}
                title={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                        <Typography variant="h6">
                            {`${translation('contact-details')}: ${kontakt.kontaktVorname || ''} ${kontakt.kontaktNachname || ''}`}
                        </Typography>
                        <KontaktActionButtons
                            kontakt={kontakt}
                            kontaktaufnahmeId={selectedKontaktaufnahme?.id}
                            kontaktVorname={kontakt.kontaktVorname}
                            kontaktNachname={kontakt.kontaktNachname}
                            kontaktAudioDatei={selectedKontaktaufnahme?.audioDatei}
                            aufgabeId={aufgabeId}
                            audioTitle={translation('download-audio')}
                            pdfTitle={translation('download-pdf')}
                            shareTitle={translation('share-link')}
                            shareCopiedTitle={translation('share-link-copied')}
                            deleteTitle={translation('delete')}
                            showDelete={false}
                        />
                    </Box>
                }
            >
                <DialogContent>
                    {unteraufgabenQueryEnabled && isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                               <CircularProgress />
                        </Box>
                      ) : (
                    <Grid container spacing={3}>
                        {isError && (
                            <Grid size={12}>
                                <Alert severity="error">
                                    {translation('error-loading')}: {String((error as any)?.message ?? error) }
                                </Alert>
                            </Grid>
                        )}
                        {/* Kontakt Details */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {translation('contact-information')}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Name:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktVorname} {kontakt.kontaktNachname}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Anrede:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktAnrede || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Telefon:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktTelefon || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>WhatsApp:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktWhatsappnummer || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Externe ID:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktExternalId || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Firma:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktFirma || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Email:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktEmail || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Feld 1:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktAufgabeFeld1 || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Feld 2:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktAufgabeFeld2 || 'N/A'}</Typography></Grid>
                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Erstellt am:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{formatDate(kontakt.kontaktCreatedAt)}</Typography></Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Kontakt Aufgabe Details */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box display="flex" justifyContent="flex-end" pb={2}>
                            <Pagination
                                page={selectedIndex + 1}
                                count={kontaktaufnahmen?.length || 1}
                                onChange={(_, page) => setSelectedIndex(page - 1)}
                            />
                        </Box>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {translation('task-information')}
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Status:</strong></Typography></Grid>
                                    <Grid size={8}>
                                        <Chip
                                            label={kontakt.kontaktAufgabeStatus || 'N/A'}
                                            color={getStatusColor(kontakt.kontaktAufgabeStatus)}
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Bevorzugter Kanal:</strong></Typography></Grid>
                                    <Grid size={8}><Typography>{kontakt.kontaktAufgabeBevorzugterKanal || 'N/A'}</Typography></Grid>
                                </Grid>
                                {kontakt?.kontaktaufnahmeStimmeName && (
                                    <Grid container spacing={1}>
                                        <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Stimme:</strong></Typography></Grid>
                                        <Grid size={8}><Typography>{kontakt.kontaktaufnahmeStimmeName}</Typography></Grid>
                                    </Grid>
                                )}
                                <Grid container spacing={1}>
                                    <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Empathie:</strong></Typography></Grid>
                                    <Grid size={8}><Typography>{kontakt.kontaktaufnahmeEmpathie ?? DEFAULT_EMPATHY }</Typography></Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Sprache:</strong></Typography></Grid>
                                    <Grid size={8}><Typography>{kontakt.kontaktaufnahmeSprache || 'N/A'}</Typography></Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Zugewiesen am:</strong></Typography></Grid>
                                    <Grid size={8}><Typography>{formatDate(kontakt.kontaktAufgabeAssignedAt)}</Typography></Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                        {/* Kontaktaufnahme Details */}
                        <Grid size={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {translation('contact-attempt-information')}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>{translation('contact-attributes-planned-contact-time')}:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{formatDate(selectedKontaktaufnahme?.startPlanned)}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>{translation('contact-attributes-actual-contact-time')}:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{formatDate(selectedKontaktaufnahme?.start)}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Ende:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{formatDate(selectedKontaktaufnahme?.ende)}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Status:</strong></Typography></Grid>
                                                <Grid size={8}>
                                                    <Chip
                                                        label={selectedKontaktaufnahme?.status || 'N/A'}
                                                        color={getStatusColor(selectedKontaktaufnahme?.status)}
                                                        size="small"
                                                    />
                                                    {selectedKontaktaufnahme?.id &&
                                                        (selectedKontaktaufnahme?.status === 'LAUFEND') && (
                                                            <StopTwilioCallButton
                                                                id={selectedKontaktaufnahme?.id}
                                                                onSuccess={()=>{}}
                                                            />
                                                        )
                                                    }
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>ID:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{selectedKontaktaufnahme?.id}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>{translation('contact-attributes-attempt-number')}:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{selectedKontaktaufnahme?.versuchNummer || 'N/A'}</Typography></Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Kanal:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{selectedKontaktaufnahme?.kanal || 'N/A'}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>{translation('call-direction')}:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{selectedKontaktaufnahme?.callDirection || 'N/A'}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Externe Call ID:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{kontakt?.kontaktaufnahmeExterneCallId || 'N/A'}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Audio Datei:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{selectedKontaktaufnahme?.audioDatei || 'N/A'}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Sprachmodell:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{selectedKontaktaufnahme?.speechModel || 'N/A'}</Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1}>
                                                <Grid size={4} sx={{ textAlign: 'right' }}><Typography><strong>Weitergeleitet an:</strong></Typography></Grid>
                                                <Grid size={8}><Typography>{selectedKontaktaufnahme?.redirectedTo || 'N/A'}</Typography></Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Transcript */}
                        {transkriptIsLoading ? (
                            <Grid size={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress />
                                </Box>
                            </Grid>
                        ) : (
                        <>
                            {(transkriptData?.transkript || transkriptData?.transkriptDeutsch) && (
                                <Grid size={12}>
                                    <TabTranscriptCard transkript={transkriptData?.transkript} transkriptDeutsch={transkriptData?.transkriptDeutsch}/>
                                </Grid>
                            )}
                        </>)}

                        {/* Auswertung Aufgabe */}
                        {!isLoading && !isError && data?.auswertungAufgabe && (
                            <Grid size={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Auswertung
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid container spacing={1}>
                                                {data.auswertungAufgabe.zusammenfassung && data.auswertungAufgabe.zusammenfassung.trim() !== '' && (
                                                    <>
                                                        <Grid size={12}>
                                                            <Typography variant="body2">
                                                                <strong>Zusammenfassung:</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, mb: 1 }}>
                                                                {data.auswertungAufgabe.zusammenfassung}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                            <Grid size={8}>
                                                <Grid container spacing={1}>

                                                    {data.auswertungAufgabe.sumGewichteteAuswertung != null && (
                                                        <>
                                                            <Grid size={8} sx={{ textAlign: 'right' }}>
                                                                <Typography><strong>Ranking (Gewichtete Auswertung):</strong></Typography>
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <Typography>{data.auswertungAufgabe.sumGewichteteAuswertung.toFixed(2)}</Typography>
                                                                    <Typography>{translation("out-of")}</Typography>
                                                                    <Typography id={"maxGewichtung"}>
                                                                        {data.unteraufgaben && data.unteraufgaben.length > 0
                                                                            ? data.unteraufgaben
                                                                                .reduce((sum, unteraufgabe) => sum + (unteraufgabe.gewichtung || 0), 0)
                                                                                .toFixed(2)
                                                                            : '0.00'
                                                                        }
                                                                    </Typography>
                                                                </Stack>
                                                            </Grid>
                                                        </>
                                                    )}

                                                    {data.auswertungAufgabe.confidenceAnalysis != null && (
                                                        <>
                                                            <Grid size={8} sx={{ textAlign: 'right' }}>
                                                                <Typography><strong>Confidence Analysis:</strong></Typography>
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <Typography>{`${(data.auswertungAufgabe.confidenceAnalysis * 100).toFixed(0)}%`}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}

                                                    {data.auswertungAufgabe.averageUnteraufgabenConfidence != null && (
                                                        <>
                                                            <Grid size={8} sx={{ textAlign: 'right' }}>
                                                                <Typography><strong>Ø Confidence:</strong></Typography>
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <Typography>{`${(data.auswertungAufgabe.averageUnteraufgabenConfidence * 100).toFixed(0)}%`}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}

                                                    {data.auswertungAufgabe.weightedAverageUnteraufgabenConfidence != null && (
                                                        <>
                                                            <Grid size={8} sx={{ textAlign: 'right' }}>
                                                                <Typography><strong>Gewichtete Ø Confidence:</strong></Typography>
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <Typography>{`${(data.auswertungAufgabe.weightedAverageUnteraufgabenConfidence * 100).toFixed(0)}%`}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}

                                                    {data.auswertungAufgabe.standardDeviationUnteraufgabenConfidence != null && (
                                                        <>
                                                            <Grid size={8} sx={{ textAlign: 'right' }}>
                                                                <Typography><strong>Streuung:</strong></Typography>
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <Typography>{`${(data.auswertungAufgabe.standardDeviationUnteraufgabenConfidence * 100).toFixed(0)}%`}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>

                                            <Grid size={4}>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Detailranking</strong>
                                                </Typography>
                                                <Table id="details-unteraufgaben" size="small">
                                                    <TableBody>
                                                        {data?.unteraufgaben && data.unteraufgaben.length > 0 ? (
                                                                data.unteraufgaben
                                                                    .filter((u) => u.gewichtung > 0)
                                                                    .map((unteraufgabe, index) => (
                                                                    <TableRow key={unteraufgabe.id || index}>
                                                                        <TableCell>{unteraufgabe.titel}</TableCell>
                                                                        <TableCell>{unteraufgabe.rankingScore?.toFixed(2)} {translation("out-of")} {unteraufgabe.gewichtung.toFixed(2)}</TableCell>
                                                                    </TableRow>
                                                            ))
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell colSpan={2}>Keine Unteraufgaben gefunden</TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Unteraufgaben */}
                        <Grid size={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {translation('subtask-evaluation')}
                                    </Typography>
                                    {isLoading && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                            <CircularProgress />
                                        </Box>
                                    )}
                                    {isError && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {error instanceof Error ? error.message : 'Fehler beim Laden der Unteraufgaben'}
                                        </Alert>
                                    )}
                                    {!isLoading && !isError && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {data?.unteraufgaben && data.unteraufgaben.length > 0 ? (
                                                data.unteraufgaben.map((unteraufgabe, index) => (
                                                    <Box key={unteraufgabe.id || index} sx={{
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 2
                                                    }}>

                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <UnteraufgabeDetailCard
                                                                    ueberschrift={translation('subtask') + ` ${unteraufgabe.reihenfolge ?? index + 1}`}
                                                                    titel={unteraufgabe.titel}
                                                                    prompt={unteraufgabe.prompt}
                                                                    beschreibung={unteraufgabe.beschreibung}
                                                                    antwort={unteraufgabe.antwort}
                                                                    antwortTyp={unteraufgabe.antwortTyp || unteraufgabe.antworttyp}
                                                                    showDetails={true}
                                                                    gewichtung={unteraufgabe.gewichtung}
                                                                    rankingScore={unteraufgabe.rankingScore}
                                                                />
                                                            </Grid>
                                                            {sprache !== DEFAULT_LANGUAGE &&(
                                                                <Grid size={{ xs: 12, md: 6 }}>
                                                                    <UnteraufgabeDetailCard
                                                                        ueberschrift={translation("in-foreign-language")}
                                                                        titel={unteraufgabe.foreignTitel}
                                                                        prompt={unteraufgabe.foreignPrompt}
                                                                        beschreibung={"N/A"}
                                                                        antwort={unteraufgabe.antwortOriginal}
                                                                        antwortTyp={unteraufgabe.antwortTyp || unteraufgabe.antworttyp}
                                                                        showDetails={false}
                                                                        gewichtung={unteraufgabe.gewichtung}
                                                                        rankingScore={unteraufgabe.rankingScore}
                                                                    />
                                                                </Grid>
                                                            )}

                                                        </Grid>
                                                        {unteraufgabe.confidenceAnalysis !== undefined && unteraufgabe.confidenceAnalysis !== null && (
                                                            <Typography variant="body2">
                                                                <strong>Confidence Score:</strong> {`${(unteraufgabe.confidenceAnalysis * 100).toFixed(2)}%`}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))
                                            ) : (
                                                <Typography color="text.secondary">
                                                    Keine Auswertung Unteraufgaben gefunden
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>)}
            </DialogContent>
        </ModalDialog>
    )
}